import React, { useMemo, useState, useRef } from "react";
import { Form } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";

import StepGradOptions from "./StepGradOptions";
import StepSchlOptions from "./StepSchlOptions";
import StepPrimeOptions from "./StepPrimeOptions";
import AlertConfirmPopup from "components/common/AlertConfirmPopup";
import AiMathExamStartPopup, { preloadAiMathExamStartImages } from "components/aicenter/AiMathExamStartPopup";

import { callPost, ConstantsServer } from "utils/CommonApi";
import Constants from "common/Constants";

import { modify } from "store/modules/common/loginReducer";
import useSpinner from "store/customHooks/useSpinner";

const StepOptions = (props) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { myInitInfo, setMyInitInfo, stepToggle, gradeCodes, primeLevelCodes, primeWeekCodes, isPrimePedcCd } = props;

    const mmbrSeq = useSelector((state) => state.loginReducer.mmbrSeq);
    const pedcCd = useSelector((state) => state.loginReducer.pedcCd);
    const userId = useSelector((state) => state.loginReducer.userId);
    const { openSpinner, closeSpinner } = useSpinner();

    /* AI수학 진단평가 시작 팝업 (응시 가능한 진단평가가 있을 때 노출) */
    const [examStartOpen, setExamStartOpen] = useState(false);
    // 시작하기 URL 조립에 사용할 진단평가 응답 정보 (SMST_CD, EXAM_SEQ)
    const [examInfo, setExamInfo] = useState(null);
    // 저장 클릭 시점에 시작한 팝업 이미지 프리로드 Promise
    const preloadPromiseRef = useRef(null);

    const headerParam = {
        user_id: userId,
    };

    // 1/1~6/29 기간 체크 (GR20 저장 버튼 노출용)
    const isBefore630 = useMemo(() => {
        const currentMonth = moment().month(); // 0부터 시작 (0=1월, 5=6월)
        const currentDate = moment().date();
        return currentMonth < 5 || (currentMonth === 5 && currentDate <= 29);
    }, []);

    // 저장 버튼만 노출 (다음 없이 바로 저장)
    // - 예비고(GR30): 항상
    // - 예비중(GR20): 6/29 이전
    // - 학교 정보 "없어요"(schlCdFlag === "N") 선택 시: 항상
    const isSaveOnly =
        myInitInfo.gradCd === "GR30" || (myInitInfo.gradCd === "GR20" && isBefore630) || myInitInfo.schlCdFlag === "N";

    // 저장 버튼 클릭 (예비중 GR20 / 예비고 GR30 전용)
    const handleClickSave = () => {
        // 저장 API 시퀀스와 병렬로 팝업 이미지 프리로드 시작 (스피너 동안 미리 로드)
        preloadPromiseRef.current = preloadAiMathExamStartImages();
        mmbrGradUpdate();
    };

    // 다음 버튼 클릭
    const handleClickNext = () => {
        if (myInitInfo.schlCd) {
            stepToggle("2");
        } else {
            callAlertPopup({
                title: "알림",
                type: "ALERT",
                msg: "학교 찾기를 통해 <br> 학교 정보를 입력해주세요.",
            })
                .then((resData) => {})
                .catch((err) => {
                    console.log("error");
                });
        }
    };

    const mmbrGradUpdate = () => {
        let queryParam = {
            MMBR_SEQ: mmbrSeq,
            GRAD_CD: myInitInfo.gradCd,
        };
        const param = {
            REQUEST_API: Constants.MEMBERSHIP_API.UPDATE_GRAD,
            HTTP_METHOD: Constants.HTTP_METHOD.PUT,
            DOMAIN: ConstantsServer.SERVER_DOMAIN.GW_SMARTALL_MID_DOMAIN,
            BODY: {
                GRAD_CD: myInitInfo.gradCd,
            },
        };
        callPost(
            headerParam,
            param,
            (resData) => {
                setRedisContext();
            },
            queryParam,
        );
    };

    // 학년 redis update
    const setRedisContext = async () => {
        let queryParam = {
            MMBR_SEQ: mmbrSeq,
            GRAD_CD: myInitInfo.gradCd,
        };
        const param = {
            URL: "/common/setRedisContext",
            HTTP_METHOD: Constants.HTTP_METHOD.POST,
            BODY: {
                GRAD_CD: myInitInfo.gradCd,
            },
        };

        await callPost(
            headerParam,
            param,
            (resData) => {
                dispatch(
                    modify({
                        gradCd: resData.GRAD_CD,
                    }),
                );
                initSaveStudyPlan();
            },
            queryParam,
        );
    };

    const buildPrimeParam = () =>
        pedcCd === "PEDC-00015" || pedcCd === "PEDC-00016"
            ? `&LVL_CD=${myInitInfo.primeLevel}&PEDC_WEEK_SEQ=${myInitInfo.primeWeek}`
            : "";

    // 표준 시간표 조회 응답 → WEEK_COURSE_LIST 가공 (월~일 순, DSP_NO 1-based)
    const buildWeekCourseList = (stdList) => {
        const WK_ORDER = ["2", "3", "4", "5", "6", "7", "1"]; // 월·화·수·목·금·토·일
        return WK_ORDER.map((wkCd) => ({
            WK_CD: wkCd,
            COURSE_LIST: stdList
                .filter((tmtble) => tmtble.WK_CD === wkCd)
                .map((tmtble, idx) => ({ COURSE_CODE: tmtble.COURSE_CODE, DSP_NO: idx + 1 })),
        }));
    };

    // 1) 표준 시간표 조회 (step1 저장 경로는 교과서 선택이 없으므로 AATB_LIST: [])
    const initSaveStudyPlan = () => {
        const queryParam = {
            GRAD_CD: myInitInfo.gradCd,
            MMBR_SEQ: mmbrSeq,
        };

        try {
            const param = {
                REQUEST_API:
                    Constants.STUDY_PLAN_API.STD_TMTLB_LIST +
                    "?TODAY=" +
                    moment().format("YYYYMMDD") +
                    "&TYPE=INIT" +
                    buildPrimeParam(),
                HTTP_METHOD: Constants.HTTP_METHOD.POST,
                BODY: {
                    AATB_LIST: [],
                },
            };

            openSpinner({ color: "#f7901e", backdropColor: "#000" });
            callPost(headerParam, param, callbackStdTmtblList, queryParam);
        } catch (e) {
            console.log("error");
            closeSpinner();
        }
    };

    // 2) 조회 결과 → 가공 후 저장
    const callbackStdTmtblList = (resData) => {
        saveTimeTable(buildWeekCourseList(resData.RESP_RESULT.STD_TMTLB_LIST));
    };

    // 3) 시간표 저장
    const saveTimeTable = (weekCourseList) => {
        const queryParam = {
            MMBR_SEQ: mmbrSeq,
            GRAD_CD: myInitInfo.gradCd,
        };

        try {
            const param = {
                REQUEST_API:
                    Constants.STUDY_PLAN_API.SAVE_STUDY_PLAN +
                    "?TODAY=" +
                    moment().format("YYYYMMDD") +
                    buildPrimeParam(),
                HTTP_METHOD: Constants.HTTP_METHOD.POST,
                BODY: {
                    SCHL_CD: myInitInfo.schlCd,
                    AATB_LIST: [],
                    WEEK_COURSE_LIST: weekCourseList,
                },
            };

            callPost(headerParam, param, callSaveEventPopup, queryParam);
        } catch (e) {
            console.log("error");
            closeSpinner();
        }
    };

    const callSaveEventPopup = () => {
        const queryParam = {
            MMBR_SEQ: mmbrSeq,
        };

        const param = {
            REQUEST_API: Constants.MID_STUDY_API.EVENT_POPUP_SAVE + `?PPUP_SEQ=60`,
            HTTP_METHOD: Constants.HTTP_METHOD.GET,
        };

        callPost(headerParam, param, callbackSaveEventPopup, queryParam);
    };

    // 저장 완료 후 마지막으로 응시 가능한 수학 진단평가 조회
    const callbackSaveEventPopup = (resData) => {
        callAbilityExam();
    };

    // 응시 가능한 수학 진단평가 조회 (마지막 호출)
    const callAbilityExam = () => {
        const queryParam = {
            MMBR_SEQ: mmbrSeq,
            PEDC_CD: pedcCd,
            GRAD_CD: myInitInfo.gradCd,
        };
        const param = {
            REQUEST_API: Constants.ABILITY_EXAM_API.AVAILABLE_EXAM,
            HTTP_METHOD: Constants.HTTP_METHOD.GET,
        };

        callPost(headerParam, param, callbackAbilityExam, queryParam);
    };

    // 응시 가능한 진단평가가 있으면(EXAM_EXIST_YN === "Y") AI수학 진단평가 시작 팝업 노출
    const callbackAbilityExam = (resData) => {
        const examInfo = resData?.RESP_RESULT;
        console.log("[StepOptions] 응시 가능한 수학 진단평가 조회 응답:", examInfo);

        // 최초입회(InitTimeTable) 경로로 메인 진입함을 표시 (메인에서 이벤트팝업 조회 시 파라미터 부착용)
        sessionStorage.setItem("afterInitTmtbl", "1");

        if (examInfo?.EXAM_EXIST_YN !== "Y") {
            // 응시 가능한 진단평가가 없으면 메인화면으로 이동
            closeSpinner();
            history.replace("/today/middleStudyToday");
            return;
        }

        // 시작하기 시 사용할 SMST_CD, EXAM_SEQ 보관
        setExamInfo(examInfo);

        // 이미지 프리로드 완료까지(최대 2500ms) 스피너 유지 후 완성된 팝업 노출
        const preload = preloadPromiseRef.current || Promise.resolve();
        const timeout = new Promise((resolve) => setTimeout(resolve, 2500));
        Promise.race([preload, timeout]).finally(() => {
            closeSpinner();
            setExamStartOpen(true);
        });
    };

    /* 공통 모달 호출 */
    const callAlertPopup = async (modalInfo) => {
        const reqData = {
            title: modalInfo.title,
            type: modalInfo.type,
            msg: modalInfo.msg,
            bigSize: modalInfo?.bigSize,
            autoWidth: modalInfo?.autoWidth,
            leftAlign: modalInfo?.leftAlign,
        };

        return await AlertConfirmPopup(reqData);
    };

    return (
        <div className="FormWrap">
            <Form className="Form">
                <div className="FormType1">
                    <StepGradOptions
                        myInitInfo={myInitInfo}
                        setMyInitInfo={setMyInitInfo}
                        gradeCodes={gradeCodes}
                        isPrimePedcCd={isPrimePedcCd}
                    />
                    <div className="groupline" />
                    <StepSchlOptions myInitInfo={myInitInfo} setMyInitInfo={setMyInitInfo} />
                    {isPrimePedcCd && (
                        <>
                            <div className="groupline" />
                            <StepPrimeOptions
                                myInitInfo={myInitInfo}
                                setMyInitInfo={setMyInitInfo}
                                primeLevelCodes={primeLevelCodes}
                                primeWeekCodes={primeWeekCodes}
                            />
                        </>
                    )}
                    <div className="btnWrap">
                        <button type="button" className="save" onClick={isSaveOnly ? handleClickSave : handleClickNext}>
                            {isSaveOnly ? "저장" : "다음"}
                        </button>
                    </div>
                </div>
            </Form>

            <AiMathExamStartPopup
                isOpen={examStartOpen}
                onClose={() => setExamStartOpen(false)}
                mmbrSeq={mmbrSeq}
                gradCd={myInitInfo.gradCd}
                smstCd={examInfo?.SMST_CD}
                examSeq={examInfo?.EXAM_SEQ}
            />
        </div>
    );
};

export default StepOptions;
